import type { ArrivalMenuState, HeldState, I9State } from '@/features/arrival/types';
import { ARRIVAL_MENU_UNREAD, HELD_STATE, I9_STATE } from '@/features/arrival/types';

import { kioskFetch } from './kioskFetch';

/**
 * The arrival menu's one read — `GET /arrival-reason/:candidateId/menu-state` [KID-5a; decision 0387].
 *
 * **It replaces two reads, and both carried more than the menu may see.** The screen read the status cards
 * (`/associate/:id/status` — client name, street address, shift) and the candidate profile
 * (`/candidates/:id/profile` — fourteen fields of the record) to derive two values. 0387 gives the menu a
 * route of its own that answers exactly `{ held, i9 }`, already reduced, so the record never reaches a
 * lobby screen. Both of those routes stay, unchanged, for the screens that own them.
 *
 * ⚠️ **Hand-written where the convention (§ 6) asks for generated types**, for the reason
 * `identityLookup.ts` records: `npm run api:types` reads `../iss-kiosk-api/openapi.json`, which the backend
 * does not emit yet. The shape is the api's `{ held, i9 }` and the value sets are pinned in
 * `features/arrival/types.test.ts`.
 */

/**
 * **Above the api's slower half-deadline, on purpose.** The api answers each half inside its own deadline
 * (8 s for `held`, 10 s for `i9`) and always with a 200 — an unread half comes back as `unknown`. A client
 * timeout below 10 s would cut off an `i9` the api was about to answer, and turn a real `held` into
 * `unknown` with it.
 */
export const REQUEST_TIMEOUT_MS = 12_000;

/** Fail open: nothing is hidden on an absence of information [KID-5b; KID-5c]. */
const UNREAD = ARRIVAL_MENU_UNREAD;

/**
 * **A failure of THIS read is said out loud, because fail-open hides it.** A missing route, a refused
 * request or a renamed field gives every candidate every row — a placed associate is offered openings
 * (BR-K6) — and the screen looks exactly as it does on a real `unknown`. The status read failed open the
 * same way and silently, and nobody knew until the Operator measured it live on 2026-09-10.
 *
 * Same shape as `arrivalReason.ts`'s `reportRefusal` (`msg` · `alert` · detail). **No candidate id**, and
 * the route is named by its template, never by the path that carries the id. A 401 is not reported here:
 * `kioskFetch` already reports it and returns the device to the PIN pad. An `unknown` the api ITSELF sent
 * is not reported either — that is the api's own verdict, and its deadline miss is logged on its side.
 */
function reportUnread(alert: string, detail: Record<string, unknown>): void {
  console.error({
    msg: 'the arrival menu could not read { held, i9 } — it is failing open and showing every row',
    alert,
    route: '/arrival-reason/:candidateId/menu-state',
    ...detail,
  });
}

function apiBaseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? '';
}

const HELD_VALUES: readonly unknown[] = Object.values(HELD_STATE);
const I9_VALUES: readonly unknown[] = Object.values(I9_STATE);

function isHeld(value: unknown): value is HeldState {
  return HELD_VALUES.includes(value);
}

function isI9(value: unknown): value is I9State {
  return I9_VALUES.includes(value);
}

/**
 * Validates at the boundary (§ 6) and keeps the two fields BY NAME. Anything else in the body is dropped
 * here, so nothing the api might add later can reach the screen through this read.
 *
 * Each half is parsed on its own, the way the api answers it: an unrecognised `i9` does not cost a good
 * `held`, and the reverse.
 */
function readMenuState(body: unknown): ArrivalMenuState {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) {
    reportUnread('arrival_menu_state_contract', { half: 'both' });
    return UNREAD;
  }
  const raw = body as Record<string, unknown>;
  if (!isHeld(raw.held)) reportUnread('arrival_menu_state_contract', { half: 'held' });
  if (!isI9(raw.i9)) reportUnread('arrival_menu_state_contract', { half: 'i9' });
  return {
    held: isHeld(raw.held) ? raw.held : HELD_STATE.unknown,
    i9: isI9(raw.i9) ? raw.i9 : I9_STATE.unknown,
  };
}

/**
 * Reads the menu's two reduced states. **Never throws** — every failure is `unknown` on both halves, which
 * the menu renders as every row it has. The caller's job is to put the candidate on a working screen
 * [BR-K7], and an exception escaping into a render tree does the opposite.
 *
 * Complexity: one request, bounded by `REQUEST_TIMEOUT_MS`.
 */
export async function fetchArrivalMenuState(candidateId: number): Promise<ArrivalMenuState> {
  let response: Response;
  try {
    response = await kioskFetch(
      `${apiBaseUrl()}/arrival-reason/${String(candidateId)}/menu-state`,
      { signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS) },
    );
  } catch (error: unknown) {
    // `TimeoutError` (the api did not answer inside 12 s) and `TypeError` (no network) mean different things.
    reportUnread('arrival_menu_state_unreachable', {
      reason: error instanceof Error ? error.name : typeof error,
    });
    return UNREAD;
  }
  if (!response.ok) {
    if (response.status !== 401) {
      reportUnread('arrival_menu_state_rejected', { status: response.status });
    }
    return UNREAD;
  }

  const body: unknown = await response.json().catch(() => undefined);
  return readMenuState(body);
}
